import React, { useState, useEffect } from 'react';
import { Phone, User, X, Plus, Loader2, Wrench, Send, Save } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { LoadingSpinner } from '../common/Badges';
import { useLang } from '../../i18n';

const AdminTechnicians = () => {
  const { t } = useLang();
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', employment_type: 'W2', hourly_rate: '', address: '' });
  const [submitting, setSubmitting] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [rateValue, setRateValue] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', profile_photo_url: '', employment_type: 'W2', address: '' });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [passwordModal, setPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => { loadTechnicians(); }, []);

  const loadTechnicians = async () => {
    try { const data = await api('/api/technicians'); setTechnicians(data); }
    catch { toast.error('Failed to load technicians'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const payload = { ...formData };
      if (payload.hourly_rate) payload.hourly_rate = parseFloat(payload.hourly_rate);
      else delete payload.hourly_rate;
      const result = await api('/api/technicians', { method: 'POST', body: JSON.stringify(payload) });
      toast.success(result.message || 'Technician created!');
      setShowForm(false); setFormData({ name: '', email: '', phone: '', employment_type: 'W2', hourly_rate: '', address: '' }); loadTechnicians();
    } catch (err) { toast.error(err.message); } finally { setSubmitting(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return null;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const url = process.env.REACT_APP_BACKEND_URL + '/api/upload';
      const res = await fetch(url, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      return data.url;
    } finally { setUploadingPhoto(false); }
  };

  const onPhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadPhoto(file);
      setEditForm(f => ({ ...f, profile_photo_url: url }));
      toast.success('Photo uploaded');
    } catch (err) { toast.error('Photo upload failed: ' + err.message); }
  };

  const saveRate = async (techId) => {
    try {
      await api(`/api/technicians/${techId}/rate`, { method: 'PUT', body: JSON.stringify({ hourly_rate: parseFloat(rateValue) }) });
      toast.success('Hourly rate updated'); setEditingRate(null); loadTechnicians();
    } catch (err) { toast.error(err.message); }
  };

  const saveEdit = async () => {
    try {
      // Update technician-specific fields via /api/technicians/{id}
      await api(`/api/technicians/${editingUser}`, { method: 'PUT', body: JSON.stringify({
        name: editForm.name,
        phone: editForm.phone,
        employment_type: editForm.employment_type,
        address: editForm.address,
        profile_photo_url: editForm.profile_photo_url,
      }) });
      toast.success('Technician updated'); setEditingUser(null); loadTechnicians();
    } catch (err) { toast.error(err.message); }
  };

  const changePassword = async () => {
    try {
      await api(`/api/admin/users/${passwordModal}/password`, { method: 'PUT', body: JSON.stringify({ new_password: newPassword }) });
      toast.success('Password changed'); setPasswordModal(null); setNewPassword('');
    } catch (err) { toast.error(err.message); }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete technician ${name}? This cannot be undone.`)) return;
    try { await api(`/api/admin/users/${id}`, { method: 'DELETE' }); toast.success('Technician deleted'); loadTechnicians(); }
    catch (err) { toast.error(err.message); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="admin-technicians" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-white">{t('tech_page_title')}</h1>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="add-technician-btn">
          <Plus className="w-4 h-4 inline mr-2" />{t('tech_add_btn')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {technicians.map((tech) => (
          <div key={tech.id} className="card p-6" data-testid={`technician-card-${tech.id}`}>
            <div className="flex items-center gap-4 mb-4">
              {(tech.profile_photo_url || tech.photo_url) ? (
                <img src={tech.profile_photo_url || tech.photo_url} alt={tech.name} className="w-12 h-12 rounded-full object-cover border-2 border-green-500" />
              ) : (
                <div className="w-12 h-12 bg-green-500 flex items-center justify-center rounded-full">
                  <Wrench className="w-6 h-6 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-white truncate">{tech.name}</p>
                <p className="text-sm text-gray-400 truncate">{tech.email}</p>
                <span className={`inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${tech.employment_type === '1099' ? 'bg-purple-900/40 text-purple-300' : 'bg-blue-900/40 text-blue-300'}`}>
                  {tech.employment_type || 'W2'}
                </span>
              </div>
            </div>
            {tech.phone && <p className="text-sm text-gray-400 flex items-center gap-2 mb-3"><Phone className="w-4 h-4" />{tech.phone}</p>}

            {/* Hourly Rate */}
            <div className="border-t border-gray-800 pt-3 mt-3">
              {editingRate === tech.id ? (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">$/hr:</span>
                  <input type="number" step="0.50" min="0" className="input flex-1 text-sm py-1.5" value={rateValue} onChange={(e) => setRateValue(e.target.value)} data-testid={`rate-input-${tech.id}`} />
                  <button onClick={() => saveRate(tech.id)} className="btn-primary text-xs px-3 py-1.5" data-testid={`save-rate-${tech.id}`}>{t('tech_rate_save')}</button>
                  <button onClick={() => setEditingRate(null)} className="text-gray-400 text-xs">X</button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">{t('tech_rate')}: <span className="text-white font-bold">${tech.hourly_rate?.toFixed(2) || '0.00'}</span>/hr</span>
                  <button onClick={() => { setEditingRate(tech.id); setRateValue(tech.hourly_rate || ''); }} className="text-xs text-red-500 hover:text-red-400" data-testid={`edit-rate-${tech.id}`}>{t('tech_rate_edit')}</button>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 mt-3 border-t border-gray-800 pt-3">
              <button onClick={() => { setEditingUser(tech.id); setEditForm({ name: tech.name, email: tech.email, phone: tech.phone || '', profile_photo_url: tech.profile_photo_url || tech.photo_url || '', employment_type: tech.employment_type || 'W2', address: tech.address || '' }); }} className="text-xs text-blue-400 hover:text-blue-300" data-testid={`edit-tech-${tech.id}`}>{t('tech_actions_edit')}</button>
              <button onClick={() => setPasswordModal(tech.id)} className="text-xs text-yellow-400 hover:text-yellow-300" data-testid={`change-pwd-${tech.id}`}>{t('tech_actions_password')}</button>
              <button onClick={() => deleteUser(tech.id, tech.name)} className="text-xs text-red-500 hover:text-red-400" data-testid={`delete-tech-${tech.id}`}>{t('tech_actions_delete')}</button>
            </div>
          </div>
        ))}
      </div>

      {technicians.length === 0 && <div className="card p-8 text-center text-gray-400">{t('tech_empty')}</div>}

      {/* Add Technician Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-md w-full" data-testid="add-technician-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-white">{t('tech_modal_add')}</h3>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-900"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-400 mb-4">{t('tech_modal_add_hint')}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" placeholder={t('tech_field_name')} className="input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="tech-name-input" />
              <input type="email" placeholder={t('tech_field_email')} className="input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="tech-email-input" />
              <input type="tel" placeholder={t('tech_field_phone')} className="input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="tech-phone-input" />
              <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="submit-tech-btn">{submitting && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}{t('tech_create_submit')}</button>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-md w-full" data-testid="edit-user-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-white">{t('tech_modal_edit')}</h3>
              <button onClick={() => setEditingUser(null)} className="p-2 hover:bg-gray-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <input type="text" placeholder={t('tech_field_name')} className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-name-input" />
              <input type="email" placeholder={t('tech_field_email_locked')} className="input opacity-60" value={editForm.email} disabled />
              <input type="tel" placeholder={t('tech_field_phone')} className="input" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-phone-input" />
              <input type="text" placeholder={t('tech_field_address')} className="input" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} data-testid="edit-tech-address" />
              <div>
                <label className="text-xs text-gray-500 font-bold tracking-wider mb-1 block">{t('tech_employment_type')}</label>
                <div className="grid grid-cols-2 gap-2">
                  {['W2', '1099'].map((typ) => (
                    <button key={typ} type="button" onClick={() => setEditForm({ ...editForm, employment_type: typ })}
                      className={`py-2 text-sm font-bold rounded border ${editForm.employment_type === typ ? 'bg-red-600 border-red-500 text-white' : 'bg-gray-900 border-gray-700 text-gray-400'}`}
                      data-testid={`edit-emp-${typ}`}>{typ}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-bold tracking-wider mb-1 block">{t('tech_profile_photo')}</label>
                <input type="file" accept="image/*" onChange={onPhotoSelect} className="text-xs text-gray-400" data-testid="edit-photo-file" />
                {uploadingPhoto && <p className="text-xs text-gray-500 mt-1"><Loader2 className="w-3 h-3 inline animate-spin" /> {t('tech_uploading')}</p>}
              </div>
              {editForm.profile_photo_url && <img src={editForm.profile_photo_url} alt="Preview" className="w-20 h-20 object-cover rounded-full mx-auto border-2 border-gray-700" />}
              <button onClick={saveEdit} className="btn-primary w-full" data-testid="save-edit-btn">{t('tech_save_changes')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {passwordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-sm w-full" data-testid="password-modal">
            <h3 className="font-heading text-xl font-bold text-white mb-4">{t('tech_modal_password')}</h3>
            <input type="text" placeholder={t('tech_new_password')} className="input mb-4" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password-input" />
            <div className="flex gap-2">
              <button onClick={changePassword} className="btn-primary flex-1" data-testid="save-password-btn">{t('save')}</button>
              <button onClick={() => { setPasswordModal(null); setNewPassword(''); }} className="btn-secondary flex-1">{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default AdminTechnicians;
